import { gql, useQuery as useAppSyncQuery } from "@apollo/client";

// Types based on the provided schema
export interface AttendanceModule {
  mnemonic: string;
  yearId?: string;
  majorDepartmentId?: string;
  courseId?: string;
  aSPId?: string;
  talisCode?: string;
  year?: string;
  shortTitle?: string;
  longTitle?: string;
  categoryLevel?: string;
  credit?: string;
  vleUrl?: string;
  departments?: Department[];
}

export interface Department {
  Identity?: string;
  Title?: string;
  IsMajorDepartment?: string;
  identity?: string;
  title?: string;
  isMajorDepartment?: string;
  inUse?: string;
  isAcademic?: string;
  mnemonic?: string;
  facultyId?: string;
  institutionId?: string;
}

export interface Location {
  PlannonId?: string;
  Description?: string;
  Longitude?: string;
  Latitude?: string;
}

export interface Absence {
  reason?: string;
  reasonId?: string;
  reasonDescription?: string;
  eventIdentity?: string;
  cid?: string;
}

export interface FailedAttempt {
  geolocation?: Geolocation;
  category?: string;
  Geolocation?: FailedAttemptGeolocation;
  Category?: string;
}

export interface FailedAttemptGeolocation {
  Accuracy?: number;
  Longitude?: number;
  Latitude?: number;
  Speed?: number;
  Altitude?: number;
  AltitudeAccuracy?: number;
  UserAgent?: string;
  Heading?: string;
  FingerPrint?: string;
  Timestamp?: string;
}

export interface Geolocation {
  accuracy?: number;
  longitude?: number;
  latitude?: number;
  speed?: number;
  altitude?: number;
  altitudeAccuracy?: number;
  userAgent?: string;
  heading?: string;
  fingerPrint?: string;
  timestamp?: string;
  Accuracy?: number;
  Longitude?: number;
  Latitude?: number;
  Speed?: number;
  Altitude?: number;
  AltitudeAccuracy?: number;
  UserAgent?: string;
  Heading?: string;
  FingerPrint?: string;
  Timestamp?: string;
}

export interface BeaconRecord {
  locationReference?: string;
}

export interface AttendanceEvent {
  uIMID?: string;
  eventIdentity?: string;
  date?: string;
  studentId?: string;
  startDateTimeUTC?: string;
  endDateTimeUTC?: string;
  registerStartTimeUTC?: string;
  registerEndTimeUTC?: string;
  eventDescription?: string;
  present?: boolean;
  cancelled?: boolean;
  blacklisted?: boolean;
  firstName?: string;
  surname?: string;
  username?: string;
  email?: string;
  teachers?: string;
  teachersGTA?: string;
  staffMember?: string;
  alternateRoom?: Location;
  absenceRecord?: Absence;
  completedInitialPushback?: boolean;
  failedAttempts?: FailedAttempt[];
  successfulAttempt?: Geolocation;
  attendancePresentStatus?: string;
  beaconRecord?: BeaconRecord;
  registerType?: string;
  locations?: Location[];
  eventType?: string;
  vleUrl?: string;
  source?: string;
  module?: AttendanceModule;
  code?: string;
}

// GraphQL query for getting attendance events by event identity
const GET_ATTENDANCE_EVENT_BY_ID = gql`
  query GetAttendanceEventById($eventIdentity: String!) {
    getAttendanceEventById(eventIdentity: $eventIdentity) {
      uIMID
      eventIdentity
      date
      studentId
      startDateTimeUTC
      endDateTimeUTC
      registerStartTimeUTC
      registerEndTimeUTC
      eventDescription
      present
      cancelled
      blacklisted
      firstName
      surname
      username
      email
      teachers
      teachersGTA
      staffMember
      alternateRoom {
        PlannonId
        Description
        Longitude
        Latitude
      }
      absenceRecord {
        reason
        reasonId
        reasonDescription
        eventIdentity
        cid
      }
      completedInitialPushback
      failedAttempts {
        geolocation {
          accuracy
          longitude
          latitude
          speed
          altitude
          altitudeAccuracy
          userAgent
          heading
          fingerPrint
          timestamp
        }
        Geolocation {
          Accuracy
          Longitude
          Latitude
          Speed
          Altitude
          AltitudeAccuracy
          UserAgent
          Heading
          FingerPrint
          Timestamp
        }
        Category
      }
      successfulAttempt {
        accuracy
        longitude
        latitude
        speed
        altitude
        altitudeAccuracy
        userAgent
        heading
        fingerPrint
        timestamp
        Accuracy
        Longitude
        Latitude
        Speed
        Altitude
        AltitudeAccuracy
        UserAgent
        Heading
        FingerPrint
        Timestamp
      }
      attendancePresentStatus
      beaconRecord {
        locationReference
      }
      registerType
      locations {
        PlannonId
        Description
        Longitude
        Latitude
      }
      eventType
      vleUrl
      source
      module {
        mnemonic
        yearId
        majorDepartmentId
        courseId
        aSPId
        talisCode
        year
        shortTitle
        longTitle
        categoryLevel
        credit
        vleUrl
        departments {
          Identity
          Title
          IsMajorDepartment
          identity
          title
          isMajorDepartment
          inUse
          isAcademic
          mnemonic
          facultyId
          institutionId
        }
      }
      code
    }
  }
`;

// GraphQL query for getting attendance events by date and location
const GET_ATTENDANCE_EVENTS_BY_DATE_AND_LOCATION = gql`
  query GetAttendanceEventsByDateAndLocation(
    $startDateTimeUTC: String!
    $endDateTimeUTC: String!
    $plannonId: String!
  ) {
    getAttendanceEventsByDateAndLocation(
      startDateTimeUTC: $startDateTimeUTC
      endDateTimeUTC: $endDateTimeUTC
      plannonId: $plannonId
    ) {
      uIMID
      eventIdentity
      date
      studentId
      startDateTimeUTC
      endDateTimeUTC
      registerStartTimeUTC
      registerEndTimeUTC
      eventDescription
      present
      cancelled
      blacklisted
      firstName
      surname
      username
      email
      teachers
      teachersGTA
      staffMember
      alternateRoom {
        PlannonId
        Description
        Longitude
        Latitude
      }
      absenceRecord {
        reason
        reasonId
        reasonDescription
        eventIdentity
        cid
      }
      completedInitialPushback
      failedAttempts {
        geolocation {
          accuracy
          longitude
          latitude
          speed
          altitude
          altitudeAccuracy
          userAgent
          heading
          fingerPrint
          timestamp
        }
        Geolocation {
          Accuracy
          Longitude
          Latitude
          Speed
          Altitude
          AltitudeAccuracy
          UserAgent
          Heading
          FingerPrint
          Timestamp
        }
        Category
      }
      successfulAttempt {
        accuracy
        longitude
        latitude
        speed
        altitude
        altitudeAccuracy
        userAgent
        heading
        fingerPrint
        timestamp
        Accuracy
        Longitude
        Latitude
        Speed
        Altitude
        AltitudeAccuracy
        UserAgent
        Heading
        FingerPrint
        Timestamp
      }
      attendancePresentStatus
      beaconRecord {
        locationReference
      }
      registerType
      locations {
        PlannonId
        Description
        Longitude
        Latitude
      }
      eventType
      vleUrl
      source
      module {
        mnemonic
        yearId
        majorDepartmentId
        courseId
        aSPId
        talisCode
        year
        shortTitle
        longTitle
        categoryLevel
        credit
        vleUrl
        departments {
          Identity
          Title
          IsMajorDepartment
          identity
          title
          isMajorDepartment
          inUse
          isAcademic
          mnemonic
          facultyId
          institutionId
        }
      }
      code
    }
  }
`;

/**
 * Hook to fetch attendance events by event identity
 * @param eventIdentity - The unique identifier for the attendance event
 * @returns Query result containing attendance events data
 */
export const useAttendanceEventById = (eventIdentity: string | null) => {
  return useAppSyncQuery<{ getAttendanceEventById: AttendanceEvent[] }>(
    GET_ATTENDANCE_EVENT_BY_ID,
    {
      variables: { eventIdentity },
      skip: !eventIdentity, // Skip query if eventIdentity is not provided
    }
  );
};

/**
 * Hook to fetch attendance events by date and location PlannonID
 * @param startDateTimeUTC - The start date/time to search for (in ISO string format)
 * @param endDateTimeUTC - The end date/time to search for (in ISO string format)
 * @param plannonId - The PlannonID of the location
 * @returns Query result containing attendance events data
 */
export const useAttendanceEventsByDateAndLocation = (
  startDateTimeUTC: string | null,
  endDateTimeUTC: string | null,
  plannonId: string | null
) => {
  return useAppSyncQuery<{
    getAttendanceEventsByDateAndLocation: AttendanceEvent[];
  }>(GET_ATTENDANCE_EVENTS_BY_DATE_AND_LOCATION, {
    variables: { startDateTimeUTC, endDateTimeUTC, plannonId },
    skip: !startDateTimeUTC || !endDateTimeUTC || !plannonId, // Skip query if any parameter is not provided
  });
};
